<template>
  <div class="gu-toaster">
    <ToastProvider :swipe-direction="swipeDirection">
      <GuToast v-for="toast in toasts" :key="toast.id" v-bind="toast">
        <template v-if="toast.variant && toast.variant !== 'default'">
          <div class="d-flex">
            <ToastDescription as-child>
              <div class="toast-body">
                <div v-html="$sanitize(toast.description)" />
                <div
                  v-if="toast.metadata"
                  class="mt-2 pt-2 border-top"
                  v-html="$sanitize(toast.metadata)"
                />
              </div>
            </ToastDescription>
            <GuToastClose class="me-2 m-auto" />
          </div>
        </template>
        <template v-else>
          <ToastTitle v-if="toast.title" as-child>
            <div class="toast-header">
              <strong class="me-auto" v-html="$sanitize(toast.title)" />
              <GuToastClose v-if="toast.dismissable" />
            </div>
          </ToastTitle>
          <ToastDescription v-if="toast.description" as-child>
            <div class="toast-body">
              <div v-html="$sanitize(toast.description)" />
              <div
                v-if="toast.metadata"
                class="mt-2 pt-2 border-top"
                v-html="$sanitize(toast.metadata)"
              />
            </div>
          </ToastDescription>
        </template>
      </GuToast>
      <ToastViewport data-gu-toaster :class="contentClass" />
    </ToastProvider>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  ToastProvider,
  ToastTitle,
  ToastDescription,
  ToastViewport,
} from 'radix-vue'

import { useToast } from './Toast'
import { GuToast, GuToastClose } from '../toast'

/* eslint-disable vue/require-default-prop */
const props = defineProps({
  side: {
    type: String,
    default: 'top-right',
    description: 'Position of the toast relative to the viewport.',
    validator: (val) => ['top-right', 'top-left', 'bottom-right', 'bottom-left'].includes(val),
  },
  class: {
    type: String,
    required: false,
  },
})

const { toasts } = useToast()

const swipeDirections = {
  'bottom-left': 'left',
  'bottom-right': 'right',
  'top-left': 'left',
  'top-right': 'right',
}

const contentClass = computed(() => [
  'toast-container list-unstyled position-fixed p-3',
  `toast-container-${props.side}`,
  props.class,
])
const swipeDirection = computed(() => swipeDirections[props.side])
</script>

<style lang="scss">
.gu-toaster {
  height: 0;

  .toast {
    &[data-state='closed'] {
      animation: fade-out 100ms ease-in;
    }

    &[data-swipe='move'] {
      transform: translateX(var(--radix-toast-swipe-move-x));
    }

    &[data-swipe='cancel'] {
      transform: translateX(0);
      transition: transform 200ms ease-out;
    }
  }
}

.toast-container {
  --viewport-padding: 1.5rem;

  &-top-left,
  &-top-right {
    top: 0;
    flex-direction: column-reverse;
  }

  &-bottom-left,
  &-bottom-right {
    bottom: 0;
  }

  &-bottom-left,
  &-top-left {
    left: 0;

    .toast {
      &[data-state='open'] {
        animation: slide-in-left 150ms cubic-bezier(0.16, 1, 0.3, 1);
      }

      &[data-swipe='end'] {
        animation: slide-out-left 100ms ease-out;
      }
    }
  }

  &-bottom-right,
  &-top-right {
    right: 0;

    .toast {
      &[data-state='open'] {
        animation: slide-in-right 350ms cubic-bezier(0.16, 1, 0.3, 1);
      }

      &[data-swipe='end'] {
        animation: swipe-out-right 150ms ease-out;
      }
    }
  }
}
</style>
